import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { toast } from "../components/common/Notifier";


const LANGUAGES = {
  en: { code: 'en', label: 'English', flag: '🇬🇧' },
  ru: { code: 'ru', label: 'Русский' },
  uk: { code: 'uk', label: 'Українська', flag: '🇺🇦' }
};

export const RegisterPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { t, language, changeLanguage } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('register.password_mismatch'));
      return;
    }

    setLoading(true);
    const result = await register(username, password, inviteCode);
    if (result.success) {
      toast.success(t('common.success'));
      navigate('/onboarding');
    } else {
      toast.error(result.error || t('error.general'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative"
      style={{
        backgroundImage: `url('/mooms.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-black/50"></div>

      <div className="relative z-10 w-full max-w-md space-y-8">
        <div className="flex justify-end gap-2">
          {Object.values(LANGUAGES).map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                language === lang.code
                  ? 'bg-white/90 text-blue-600 shadow-lg'
                  : 'bg-white/30 text-white hover:bg-white/50'
              }`}
            >
              {lang.flag} {lang.label}
            </button>
          ))}
        </div>

        <div className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/moom1.png"
              alt="Moomintroll"
              className="w-32 h-32 rounded-full shadow-2xl border-4 border-white hover:scale-110 transition-transform duration-300 object-cover"
            />
          </div>
          <h1 className="text-4xl font-bold text-white drop-shadow-lg">MoomDoom</h1>
          <p className="mt-2 text-lg text-white/90 drop-shadow-lg">
            {t('register.title')}
          </p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                🔑 {t('register.invite_code')}
              </label>
              <input
                type="text"
                required
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-xl bg-white/80 focus:ring-2 focus:ring-blue-500"
                placeholder={t('register.invite_code_placeholder')}
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('register.invite_code_hint')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                👤 {t('login.username')}
              </label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-xl bg-white/80 focus:ring-2 focus:ring-blue-500"
                placeholder={t('login.username_placeholder')}
                autoComplete="username"
              />
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700">
                🔒 {t('login.password')}
              </label>
              <div className="relative mt-1">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-3 py-3 pr-12 border border-gray-300 rounded-xl bg-white/80 focus:ring-2 focus:ring-blue-500"
                  placeholder={t('login.password_placeholder')}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                🔒 {t('register.confirm_password')}
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-xl bg-white/80 focus:ring-2 focus:ring-blue-500"
                placeholder={t('register.confirm_password_placeholder')}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
            >
              {loading ? t('common.loading') : `✍️ ${t('register.register_button')}`}
            </button>
          </form>

          <div className="text-center mt-4">
            <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800">
              {t('register.have_account')}
            </Link>
          </div>
        </div>

        <div className="text-center mt-4">
          <Link to="/" className="text-sm text-blue-600 hover:text-blue-800">
            ← На главную
          </Link>
        </div>
      </div>
    </div>
  );
};